import { Alert, Button } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserDetailsContext } from '../../../context/user-details/user-details'

export const invitedOrientationStorageKey = (userId: string) => `invitedOrientationPending:${userId}`

/**
 * One-time orientation for users who joined through an invite and land on the
 * dashboard with no idea what their role means. The invited-signup page sets a
 * per-user localStorage flag the moment signup completes (the only place that
 * knows this was an invite; the session payload does not carry invite fields),
 * and this banner renders while the flag is present, clearing it on dismiss.
 */
export const InvitedUserOrientationBanner = () => {
  const navigate = useNavigate()
  const userDetails = useContext(UserDetailsContext).userDetails
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (!userDetails?.id) return
    setPending(localStorage.getItem(invitedOrientationStorageKey(userDetails.id)) === 'true')
  }, [userDetails?.id])

  if (!userDetails || !pending) return null

  const dismiss = () => {
    localStorage.removeItem(invitedOrientationStorageKey(userDetails.id))
    setPending(false)
  }

  const isDataOwner = userDetails.roles.includes('data-owner')
    && !userDetails.roles.includes('admin') && !userDetails.roles.includes('analyst')
  const isClientOnly = userDetails.roles.includes('client')
    && !userDetails.roles.includes('admin') && !userDetails.roles.includes('analyst') && !userDetails.roles.includes('data-owner')

  const message = isDataOwner
    ? "You've been added as a data owner. Your tasks live in My Tasks, and this dashboard shows the figures for your sites."
    : isClientOnly
      ? "You've been added with view access. Analytics and reports are yours to explore from the navigation on the left."
      : `You've been added to ${userDetails.organisation?.name || 'your organisation'}. Emissions, analytics and reporting all live in the navigation on the left.`

  const action = isDataOwner
    ? { label: 'Go to My Tasks', path: '/my-tasks' }
    : isClientOnly
      ? { label: 'View analytics', path: '/analytics/dashboard' }
      : { label: 'View emissions', path: '/emissions/overview' }

  return (
    <Alert
      severity='info'
      action={
        <>
          <Button variant='text' size='small' onClick={() => { dismiss(); navigate(action.path) }}>
            {action.label}
          </Button>
          <Button variant='text' size='small' onClick={dismiss} sx={{ color: 'text.secondary' }}>
            Dismiss
          </Button>
        </>
      }
      icon={false}
      sx={{ mb: 2, alignItems: 'center' }}>
      {message}
    </Alert>
  )
}
